import type React from 'react';
import { Suspense, useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import Alert from '@mui/material/Alert';
import axios from 'axios';
import { EmailForm } from './Email/EmailForm';
import type { EmailFormDefaultValues } from './Email/EmailForm';
import { EmailFormLayout } from './Email/EmailFormLayout';
import { useCurrentUser } from 'providers/UserProvider';
import { contentQueries } from 'services/content/contentQueries';
import { useGetSigneesQuery } from 'hooks/useGetSigneesQuery';
import { useShareDocumentMutation } from 'hooks/useShareDocumentMutation';
import { openNotification } from 'components/notification';
import type { ShareDocumentApiPayload } from 'services/documents/DocumentRepository';
import { useTranslation } from 'react-i18next';
import { signeesQueries } from 'hooks/signeesQueries';
import type { SigneeEntity } from 'services/entities/SigneeEntity';
import { ContentType } from 'utils/getContentType';

const EmailFormErrorFallback: React.FC = () => {
  const { t } = useTranslation();
  return <Alert severity="error">{t('share_document_v3.email_share_modal.error_loading')}</Alert>;
};

interface ShareEmailDocumentProps {
  contentId: string;
  onSuccess: () => void;
}

export function ShareEmailDocument({ contentId, onSuccess }: ShareEmailDocumentProps) {
  const { data: currentUser } = useCurrentUser();

  if (!currentUser) return <EmailFormLayout.Root isLoading />;

  return (
    <Suspense fallback={<EmailFormLayout.Root isLoading />}>
      <ErrorBoundary fallbackRender={() => <EmailFormErrorFallback />}>
        <ShareEmailDocumentContent contentId={contentId} onSuccess={onSuccess} currentUser={currentUser} />
      </ErrorBoundary>
    </Suspense>
  );
}

interface ShareEmailDocumentContentProps {
  contentId: string;
  onSuccess: () => void;
  currentUser: ReturnType<typeof useCurrentUser>['data'];
}

function ShareEmailDocumentContent({ contentId, onSuccess, currentUser }: ShareEmailDocumentContentProps) {
  const { t } = useTranslation();
  const { data: documentData } = useSuspenseQuery(contentQueries.contentMetadataById(ContentType.DOCUMENT, contentId));
  const { data: signeesResponse } = useGetSigneesQuery(currentUser.accountId);
  const signeeOptions = signeesResponse.signees;

  const contact = documentData?.prospect?.contact ?? null;

  const shareDocument = useShareDocumentMutation();
  const { isPending: isSending, mutateAsync } = shareDocument;

  const documentTitleAndCompanyName = useMemo(() => {
    const title = documentData?.title ?? t('editor.default_document_title');
    return `${title} ${currentUser?.companyName ? `- ${currentUser?.companyName}` : ''}`.trim();
  }, [documentData, currentUser]);

  const onSubmit = async (data: ShareDocumentApiPayload, fromEmails: string[], toEmails: string[]) => {
    try {
      await mutateAsync(
        {
          contentId: contentId,
          fromEmails: fromEmails,
          toEmails: toEmails,
          shareDocumentPayload: data,
        },
        {
          onSuccess: () => {
            onSuccess();
          },
        }
      );
    } catch (e) {
      const isForbidden = axios.isAxiosError(e) && e.response?.status === 403;
      openNotification({
        title: isForbidden ? t('access.no_permission_action') : t('share.form.notification.error.title'),
        description: '',
        type: 'error',
      });
    }
  };

  const baseFormProps = {
    accountId: currentUser.accountId,
    handleSubmit: onSubmit,
    isSending,
    signeeOptions,
    defaultValues: {
      from: currentUser.email ?? '',
      subject: documentTitleAndCompanyName,
      body: '',
    },
  };

  if (!contact?.email?.trim()) {
    return <EmailForm {...baseFormProps} />;
  }

  const contactInOptions = signeeOptions.find((s) => s.email.toLowerCase() === contact.email.toLowerCase());
  if (contactInOptions) {
    const contactWithValue = { ...contactInOptions, value: contactInOptions.email };
    return <EmailForm {...baseFormProps} defaultValues={{ ...baseFormProps.defaultValues, to: [contactWithValue] }} />;
  }

  return (
    <Suspense fallback={<EmailFormLayout.Root isLoading />}>
      <ShareEmailWithContact contact={contact} {...baseFormProps} />
    </Suspense>
  );
}

interface ShareEmailWithContactProps {
  contact: { email: string; name: string };
  accountId: number;
  signeeOptions: SigneeEntity[];
  isSending: boolean;
  handleSubmit: (data: ShareDocumentApiPayload, fromEmails: string[], toEmails: string[]) => void;
  defaultValues: Omit<EmailFormDefaultValues, 'to'>;
}

function ShareEmailWithContact({ contact, accountId, defaultValues, ...rest }: ShareEmailWithContactProps) {
  const { data } = useSuspenseQuery(signeesQueries.search(accountId, contact.email));

  const defaultTo = useMemo(
    () =>
      (data?.signees.filter((s) => s.email.toLowerCase() === contact.email.toLowerCase()) ?? []).map((s) => ({
        ...s,
        value: s.email,
      })),
    [data, contact.email]
  );

  return <EmailForm accountId={accountId} defaultValues={{ ...defaultValues, to: defaultTo }} {...rest} />;
}
